// SEO-optimized content for all service-specific landing pages
// Each service targets specific long-tail keywords for "Harrow physiotherapy"

export const servicesData = {
  'sports-injury-rehabilitation': {
    name: 'Sports Injury Rehabilitation',
    icon: 'Activity',
    title: 'Sports Injury Physiotherapy in Harrow | Runners, Football, Tennis & More',
    metaDescription: 'Specialist sports injury rehabilitation in Harrow. MSc Sports Medicine qualified. Treatment for runners, football, tennis, rugby & climbing injuries. Same-day appointments.',
    keywords: 'sports physio Harrow, sports injury rehabilitation, sports physiotherapy Harrow, running injury physio, football injury treatment',
    heroLine: 'Specialist sports injury rehabilitation in Harrow from an MSc Sports Medicine qualified physiotherapist.',
    overview: "Sports injuries demand specialist physiotherapy, not generic treatment. Whether you're a competitive athlete, weekend warrior, runner, footballer or weekend tennis player, the difference between a quick return to performance and a long, frustrating recovery comes down to one thing: the quality of your rehabilitation programme.",
    secondPara: "At MSK and Sports Physio in Harrow, our founder Param holds an MSc in Sports Medicine and has worked with athletes across multiple disciplines — from elite-level marathon runners to recreational rugby players and competitive climbers. We don't just settle your symptoms — we build a step-by-step return-to-sport plan with clear milestones and testing.",
    benefits: [
      'MSc Sports Medicine qualified physiotherapist',
      'Detailed biomechanical and sport-specific assessment',
      'Progressive loading programmes (eccentric, isometric, plyometric)',
      'Running gait and movement analysis',
      'Return-to-sport testing with clear criteria',
      'Injury prevention programmes',
      'Direct liaison with coaches, surgeons and consultants',
    ],
    process: [
      { title: 'Initial Assessment', desc: 'Full sport-specific assessment of injury, biomechanics, training load and goals.' },
      { title: 'Diagnosis & Plan', desc: 'Clear diagnosis with timelines, treatment plan and return-to-sport milestones.' },
      { title: 'Hands-On Treatment', desc: 'Manual therapy, soft tissue work and taping to control symptoms early.' },
      { title: 'Progressive Loading', desc: 'Structured strength and conditioning programme that progresses with healing.' },
      { title: 'Return to Sport', desc: 'Sport-specific drills, plyometric work and functional testing before clearance.' },
      { title: 'Re-Injury Prevention', desc: 'Ongoing maintenance programme to keep you performing and prevent recurrence.' },
    ],
    suitableFor: [
      'Runners (5k, 10k, half-marathon, marathon)',
      'Football, rugby and team sport athletes',
      'Tennis, squash and racket sport players',
      'Climbers and outdoor athletes',
      'CrossFit, weightlifting and gym-goers',
      'Cyclists and triathletes',
      'Cricketers and golfers',
      'Recreational athletes returning after injury',
    ],
    faqs: [
      {
        q: 'I have a race coming up — can you help me train through this injury?',
        a: "Often yes. Smart load management lets many athletes continue training while rehabbing. We'll assess what's safe and structure your rehab around your event date.",
      },
      {
        q: 'Do you do running gait analysis?',
        a: 'Yes. We do detailed running assessment to identify biomechanical factors driving recurring injuries — and prescribe specific drills to address them.',
      },
      {
        q: 'How long does sports injury rehab typically take?',
        a: 'Acute soft tissue injuries: 2-6 weeks. Tendinopathies: 6-12 weeks. Complex injuries (ACL, fractures): 3-9 months of structured rehab.',
      },
    ],
  },

  'msk-physiotherapy': {
    name: 'MSK Physiotherapy',
    icon: 'Stethoscope',
    title: 'MSK Physiotherapy in Harrow | Musculoskeletal Pain & Joint Treatment',
    metaDescription: 'Expert MSK physiotherapy in Harrow. Evidence-based treatment for back pain, knee pain, shoulder & joint conditions. HCPC registered. Same-day appointments.',
    keywords: 'MSK physio Harrow, musculoskeletal physiotherapy, joint pain treatment Harrow, back pain physio, knee pain Harrow',
    heroLine: 'Evidence-based musculoskeletal (MSK) physiotherapy in Harrow for back, neck, joint and chronic pain.',
    overview: "Musculoskeletal (MSK) pain affects everyone at some point — from sudden back spasms and stiff necks to ongoing knee problems and arthritic joints. MSK physiotherapy is the gold-standard, first-line treatment for almost all of these conditions, recommended by NICE guidelines and the NHS over surgery and long-term medication.",
    secondPara: "At MSK and Sports Physio in Harrow, we provide thorough, evidence-based MSK treatment. We don't just chase pain — we assess the whole movement system, identify the underlying cause, and build a clear, time-bound plan that combines hands-on therapy with active rehabilitation.",
    benefits: [
      'NICE-compliant evidence-based treatment',
      'Detailed biomechanical and postural assessment',
      'Hands-on manual therapy and joint mobilisation',
      'Targeted exercise prescription',
      'Pain education and self-management strategies',
      'Ergonomic and lifestyle advice',
      'Clear treatment timelines',
    ],
    process: [
      { title: 'Subjective History', desc: "We listen — about your pain, lifestyle, work, hobbies and what's important to you." },
      { title: 'Objective Assessment', desc: 'Movement screening, strength testing, joint mobility and postural analysis.' },
      { title: 'Diagnosis & Education', desc: 'Clear explanation of what we found and what it means in plain English.' },
      { title: 'Hands-On Treatment', desc: 'Manual therapy, soft tissue work, mobilisation, and pain-relief techniques.' },
      { title: 'Active Rehabilitation', desc: 'Tailored exercise programme — progressive, achievable and tracked.' },
      { title: 'Long-Term Wellness', desc: 'Strategies to prevent recurrence and keep you moving well.' },
    ],
    suitableFor: [
      'Back pain (lower, mid and upper)',
      'Neck pain and headaches',
      'Shoulder pain and frozen shoulder',
      'Knee, hip and ankle pain',
      'Arthritis and joint stiffness',
      'Sciatica and nerve pain',
      'Postural and desk-related pain',
      'Chronic and recurrent musculoskeletal conditions',
    ],
    faqs: [
      {
        q: 'Do I need a GP referral for MSK physiotherapy?',
        a: "No. You can self-refer directly to private MSK physiotherapy. We're recognised by BUPA, AXA, WPA, Vitality and Aviva.",
      },
      {
        q: 'How many sessions will I need?',
        a: 'Most MSK conditions resolve in 4-8 sessions. We give you a clear plan after the initial assessment, with built-in review points.',
      },
      {
        q: 'Will I need a scan or X-ray?',
        a: "Most MSK conditions don't need imaging — we can usually diagnose clinically. If imaging is indicated, we'll guide you on the right next step.",
      },
    ],
  },

  'post-operative-rehabilitation': {
    name: 'Post-Operative Rehabilitation',
    icon: 'Stethoscope',
    title: 'Post-Operative Physiotherapy in Harrow | Knee, Hip, Shoulder & Spinal Surgery Rehab',
    metaDescription: 'Expert post-op physio in Harrow. Joint replacement, ACL, rotator cuff, spinal surgery rehab. Surgeon-aligned protocols. MSc qualified. Book today.',
    keywords: 'post op physio Harrow, post-operative rehabilitation, knee replacement rehab Harrow, hip replacement physio, ACL surgery rehab',
    heroLine: 'Surgeon-aligned post-operative rehabilitation in Harrow to maximise your surgical outcome.',
    overview: "A successful operation is only half the story — the quality of your post-operative rehabilitation is what determines whether you make a full functional recovery or struggle with stiffness, weakness and ongoing pain. Whether you've had a joint replacement, ligament reconstruction, rotator cuff repair or spinal surgery, structured physio is critical.",
    secondPara: "At MSK and Sports Physio in Harrow, post-operative rehab is one of our specialist areas. We work directly with leading London orthopaedic surgeons, follow each protocol precisely, and tailor every programme to your specific goals — whether that's walking pain-free, returning to elite sport, or just getting back to daily life with confidence.",
    benefits: [
      'Rehabilitation as per your surgeon\'s plan',
      'Phased rehabilitation with clear milestones',
      'Swelling, scar and pain management',
      'Progressive strengthening through full range of motion',
      'Functional and sport-specific re-training',
      'Final clearance testing before discharge',
    ],
    process: [
      { title: 'Pre-Op Prehab (Optional)', desc: 'Optimise strength, mobility and pain control before surgery — improves post-op outcomes dramatically.' },
      { title: 'Early Recovery (Weeks 0-6)', desc: 'Swelling control, gentle ROM, scar management, protected weight-bearing.' },
      { title: 'Progressive Strengthening', desc: 'Build back muscle, joint stability and balance through targeted exercises.' },
      { title: 'Functional Rehab', desc: 'Real-world movements — stairs, lifting, walking distances, sport-specific drills.' },
      { title: 'Return to Activity', desc: 'Progressive load testing for return to work, sport or daily activities.' },
      { title: 'Discharge & Maintenance', desc: 'Final assessment, ongoing maintenance programme, long-term injury prevention.' },
    ],
    suitableFor: [
      'Total or partial knee replacement (TKR/UKR)',
      'Total hip replacement (THR)',
      'ACL / PCL / meniscus reconstruction',
      'Rotator cuff repair',
      'Shoulder stabilisation surgery',
      'Spinal surgery (discectomy, fusion, laminectomy)',
      'Hand, wrist, foot and ankle surgery',
      'Arthroscopic / keyhole procedures',
    ],
    faqs: [
      {
        q: 'When should I start physio after surgery?',
        a: "As soon as your surgeon allows — typically within the first 1-2 weeks. Early, controlled rehab dramatically improves long-term outcomes.",
      },
      {
        q: 'Does my insurance cover post-op physiotherapy?',
        a: "Most private medical insurance covers post-op rehab when the surgery was insurance-authorised. We work with BUPA, AXA, WPA, Vitality and Aviva.",
      },
      {
        q: 'How long does post-op rehab take?',
        a: 'Hip/knee replacement: 3-6 months. ACL reconstruction: 3-9 months. Rotator cuff repair: 4-6 months. Spinal surgery: 3-9 months depending on procedure.',
      },
    ],
  },

  'paediatric-sports-physio': {
    name: 'Paediatric Sports Physio',
    icon: 'Activity',
    title: 'Paediatric Sports Physiotherapy in Harrow | Children & Teen Athletes',
    metaDescription: 'Specialist paediatric sports physio in Harrow. Treatment for growing pains, youth sports injuries, Osgood-Schlatter, Severs disease & teen athletes. Book today.',
    keywords: 'paediatric physio Harrow, children sports injury treatment, youth physiotherapy Harrow, teen athlete physio, growing pains treatment',
    heroLine: 'Specialist paediatric physiotherapy in Harrow for young athletes, growing children and active teens.',
    overview: "Children and adolescents are not just little adults — they have specific musculoskeletal needs related to their growth, development and the way they play sport. From Osgood-Schlatter's disease and Sever's heel pain to overuse injuries in young athletes, paediatric physiotherapy requires specialist expertise.",
    secondPara: "At MSK and Sports Physio in Harrow, Param has a special interest in paediatric and adolescent sports medicine. We use age-appropriate, engaging treatment approaches that work for kids — making rehab enjoyable rather than a chore. We also work closely with parents and coaches to manage training load and prevent recurrence.",
    benefits: [
      'Specialist paediatric and adolescent expertise',
      'Age-appropriate communication and treatment',
      'Engaging, game-like rehab exercises',
      'Parent and coach collaboration',
      'Training load management',
      'Growth-related injury expertise',
      'Long-term athletic development advice',
    ],
    process: [
      { title: 'Family-Friendly Assessment', desc: "Thorough assessment with the child or teen, including parents/carers — explained simply." },
      { title: 'Diagnosis & Education', desc: 'Clear explanation of what is happening in age-appropriate language for both parent and child.' },
      { title: 'Engaging Treatment', desc: "Hands-on therapy combined with game-like, age-appropriate exercises that keep kids motivated." },
      { title: 'Activity Modification', desc: 'Smart load management — sometimes that means temporary modification of sport, not stopping entirely.' },
      { title: 'Return-to-Play Planning', desc: 'Clear criteria for returning to full sport safely, with milestone tracking.' },
      { title: 'Athletic Development', desc: 'Long-term advice on training load, recovery and injury prevention for growing athletes.' },
    ],
    suitableFor: [
      "Osgood-Schlatter's disease (knee)",
      "Sever's disease (heel pain)",
      'Sinding-Larsen-Johansson syndrome',
      'Apophysitis (growth plate-related pain)',
      'Adolescent back pain and Scheuermann\'s',
      'Youth sports injuries (football, rugby, netball, gymnastics)',
      'Overuse injuries in young athletes',
      'Post-fracture rehabilitation',
    ],
    faqs: [
      {
        q: 'My child has growing pains — when should we see a physio?',
        a: "If pain is affecting sport, sleep, daily activities, or persisting more than 2 weeks, a physio assessment is sensible. Many 'growing pains' are actually treatable conditions.",
      },
      {
        q: 'Should my child stop their sport?',
        a: "Usually no — total rest is rarely the right answer. We use smart load management to keep kids playing where safe, while addressing the underlying issue.",
      },
      {
        q: 'At what age can children have physiotherapy?',
        a: "We treat children from age 8 upwards. Younger children we'd usually refer to paediatric specialists.",
      },
    ],
  },

  'sports-massage-therapy': {
    name: 'Sports Massage Therapy',
    icon: 'Heart',
    title: 'Sports Massage in Harrow | Deep Tissue & Therapeutic Massage',
    metaDescription: 'Expert sports massage in Harrow. Deep tissue, trigger point release & soft tissue therapy for athletes, runners & desk workers. Book today - 07747075809.',
    keywords: 'sports massage Harrow, deep tissue massage Harrow, soft tissue therapy, sports massage near me, trigger point release Harrow',
    heroLine: 'Therapeutic sports massage in Harrow — deep tissue, trigger point release and soft tissue therapy by a qualified physiotherapist.',
    overview: "Sports massage is therapeutic, targeted soft tissue work designed to address specific muscle tightness, fascial restrictions and trigger points. Unlike spa-style relaxation massage, sports massage is firm, focused and outcome-driven — whether you're an athlete preparing for a race, recovering from one, or a desk worker carrying chronic tension.",
    secondPara: "At MSK and Sports Physio in Harrow, sports massage is delivered by Param, a fully qualified, HCPC-registered physiotherapist — not just a massage therapist. That means every session is informed by anatomy, biomechanics and clinical reasoning, ensuring you get the right technique for your specific needs.",
    benefits: [
      'Delivered by a qualified MSc physiotherapist',
      'Clinical assessment before treatment',
      'Targeted to your specific issues',
      'Combines multiple techniques (deep tissue, trigger point, myofascial release)',
      'Standalone or integrated with full physio plan',
      'Pre-event, post-event and maintenance options',
      'Insurance-recognised when part of physio treatment',
    ],
    process: [
      { title: 'Brief Assessment', desc: 'Quick discussion of your goals, recent training, areas of tension, and any injuries.' },
      { title: 'Targeted Treatment', desc: 'Combination of deep tissue, trigger point release, myofascial release and stretching techniques.' },
      { title: 'Specific Areas Worked', desc: 'Common areas: lower back, glutes, hamstrings, calves, neck, shoulders, IT bands — based on your needs.' },
      { title: 'Aftercare Advice', desc: 'Hydration, post-massage stretches, and recovery recommendations.' },
    ],
    suitableFor: [
      'Runners and triathletes (calf, IT band, hamstring tightness)',
      'Cyclists (lower back, glutes, hip flexors)',
      'Pre-event preparation (1-3 days before)',
      'Post-event recovery (24-72 hours after)',
      'Desk workers with chronic neck/shoulder tension',
      'Anyone with muscle knots, trigger points or fascial restrictions',
      'Maintenance for regular gym-goers',
      'Post-injury soft-tissue work alongside physio',
    ],
    faqs: [
      {
        q: 'What is the difference between sports massage and physiotherapy?',
        a: 'Sports massage focuses on soft tissue. Physiotherapy is a broader discipline including assessment, manual therapy, exercise prescription and rehabilitation. Many of our patients benefit from a combination of both.',
      },
      {
        q: 'How often should I get a sports massage?',
        a: "It depends on your training load and goals. Most active people benefit from monthly maintenance massages. Athletes in heavy training may benefit from fortnightly sessions.",
      },
      {
        q: 'Should I get a massage before or after a race?',
        a: "Pre-event massage (1-3 days before): light, energising, no deep work. Post-event massage (24-72 hours after): deeper, recovery-focused. Avoid deep tissue immediately before or after.",
      },
    ],
  },

  'home-visits': {
    name: 'Home Visit Physiotherapy',
    icon: 'Home',
    title: 'Home Visit Physiotherapy in Harrow | At-Home Physio Appointments',
    metaDescription: 'Expert home visit physiotherapy in Harrow & surrounding areas. Ideal for post-op recovery, elderly, mobility issues. MSc qualified physio comes to you.',
    keywords: 'home visit physio Harrow, at home physiotherapy, mobile physio Harrow, domiciliary physiotherapy, physio home visits',
    heroLine: 'Expert physiotherapy delivered to your door across Harrow and surrounding areas.',
    overview: "Not everyone can comfortably travel to a clinic — and you shouldn't have to. Whether you're recovering from major surgery, managing limited mobility, or caring for an elderly parent, our home visit physiotherapy service brings expert care to you.",
    secondPara: "You get the full clinical experience including assessment, hands-on treatment and exercise prescription. We cover Harrow, Wealdstone, Kenton, Pinner, Stanmore, Wembley and surrounding areas.",
    benefits: [
      'Same MSc qualified physiotherapist who treats at our clinic',
      'No travel, parking or waiting room stress',
      'Treatment in your own environment (helps with real-world function)',
      'Ideal for post-op, elderly, and mobility-limited patients',
      'Family members can be present',
      'Insurance-recognised where applicable',
    ],
    process: [
      { title: 'Initial Phone Call', desc: 'Free 10-minute call to understand your needs and confirm we can help at home.' },
      { title: 'In-Home Assessment', desc: 'Comprehensive 60-minute assessment in your home including history, examination and diagnosis.' },
      { title: 'Hands-On Treatment', desc: 'Manual therapy, joint mobilisation, soft tissue work — full clinical treatment.' },
      { title: 'Home-Adapted Exercises', desc: 'Exercises designed around your home setup — using your stairs, sofa, walls and what you already have.' },
      { title: 'Follow-Up Visits', desc: 'Regular follow-up appointments scheduled to fit around your routine and recovery.' },
    ],
    suitableFor: [
      'Post-operative recovery (knee/hip replacement, fractures, ACL surgery)',
      'Elderly patients with limited mobility',
      'Patients with severe pain making travel difficult',
    ],
    faqs: [
      {
        q: 'What areas do you cover for home visits?',
        a: "We cover Harrow, Wealdstone, Kenton, Pinner, Stanmore, Wembley, Northwood, Edgware, Hatch End, Pinner, Ruislip and surrounding areas. Call 07747075809 to confirm your postcode is covered.",
      },
      {
        q: 'How much does a home visit cost?',
        a: "Home visits include a small travel fee on top of standard session pricing. Call us for an exact quote based on your location. Many insurance providers cover home visits when clinically indicated.",
      },
      {
        q: 'Can family members be present during the session?',
        a: "Absolutely — and we encourage it. For elderly or post-op patients especially, having a family member learn the home exercises alongside the patient makes recovery much more successful.",
      },
    ],
  },
};

export const serviceSlugs = Object.keys(servicesData);
